import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { clearUser, setUser } from '../lib/sentry';

/**
 * User information interface
 */
export interface User {
	id: string;
	email: string;
	name?: string;
	picture?: string;
	roles: string[];
}

/**
 * Authentication state interface
 */
export interface AuthState {
	/** Whether user is currently authenticated */
	isAuthenticated: boolean;
	/** Current user information */
	user: User | null;
	/** JWT access token */
	token: string | null;
	/** Whether authentication is being initialized */
	isLoading: boolean;
}

/**
 * Authentication context interface
 */
export interface AuthContextType extends AuthState {
	/** Login with Google ID token */
	login: (idToken: string) => Promise<void>;
	/** Logout and clear session */
	logout: () => Promise<void>;
	/** Set authentication state */
	setAuth: (user: User, token: string) => void;
	/** Clear authentication state */
	clearAuth: () => void;
	/** Check if user has specific role */
	hasRole: (role: string) => boolean;
	/** Check if user has any of the specified roles */
	hasAnyRole: (roles: string[]) => boolean;
	/** Check if user has specific permission */
	hasPermission: (permission: string) => boolean;
	/** Check if user is admin */
	isAdmin: () => boolean;
	/** Check if user can write (create/edit) */
	canWrite: () => boolean;
	/** Check if user can execute workflows */
	canExecute: () => boolean;
	/** Check if user can delete workflows (ADMIN only) */
	canDelete: () => boolean;
}

/**
 * Authentication context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props for AuthProvider component
 */
interface AuthProviderProps {
	children: ReactNode;
}

/**
 * Authentication provider component
 *
 * @description Manages authentication state and provides auth functions
 * to child components. Handles JWT token storage, automatic logout on
 * token expiration, and authentication state persistence.
 *
 * Features:
 * - JWT token management with memory storage
 * - Automatic token refresh via API interceptor
 * - Persistent authentication state
 * - Google OAuth integration
 * - Secure logout with backend session revocation
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <YourAppComponents />
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
export function AuthProvider({ children }: AuthProviderProps) {
	const [authState, setAuthState] = useState<AuthState>({
		isAuthenticated: false,
		user: null,
		token: null,
		isLoading: true,
	});

	/**
	 * Initialize authentication state on app startup
	 * Checks for existing token and validates it
	 */
	useEffect(() => {
		initializeAuth();
	}, []);

	/**
	 * Initialize authentication from stored token
	 */
	const initializeAuth = async () => {
		try {
			// Check if there's a stored token (in memory or sessionStorage)
			const storedToken = sessionStorage.getItem('auth_token');
			const storedUser = sessionStorage.getItem('auth_user');

			if (storedToken && storedUser) {
				const user = JSON.parse(storedUser);

				// Set user context in Sentry
				setUser({
					id: user.id,
					email: user.email,
					name: user.name,
					roles: user.roles,
				});

				setAuthState({
					isAuthenticated: true,
					user,
					token: storedToken,
					isLoading: false,
				});
			} else {
				setAuthState(prev => ({ ...prev, isLoading: false }));
			}
		} catch (error) {
			console.error('Failed to initialize auth:', error);
			setAuthState(prev => ({ ...prev, isLoading: false }));
		}
	};

	/**
	 * Login with Google ID token
	 *
	 * @param idToken - Google ID token from OAuth flow
	 */
	const login = async (idToken: string): Promise<void> => {
		try {
			// Call backend auth endpoint
			const response = await fetch(
				`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/v1/auth/login`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include', // Include cookies for refresh token
					body: JSON.stringify({ idToken }),
				}
			);

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.message || 'Login failed');
			}

			const authResult = await response.json();

			// Store authentication data
			setAuth(authResult.user, authResult.accessToken);
		} catch (error) {
			console.error('Login failed:', error);
			throw error;
		}
	};

	/**
	 * Logout user and revoke session
	 */
	const logout = async (): Promise<void> => {
		try {
			// Call backend logout endpoint
			await fetch(
				`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/v1/auth/logout`,
				{
					method: 'POST',
					credentials: 'include', // Include refresh token cookie
				}
			);
		} catch (error) {
			console.error('Logout error:', error);
			// Continue with local logout even if backend call fails
		}

		// Clear local authentication state
		clearAuth();
	};

	/**
	 * Set authentication state
	 *
	 * @param user - User information
	 * @param token - JWT access token
	 */
	const setAuth = (user: User, token: string): void => {
		// Store in memory and sessionStorage
		sessionStorage.setItem('auth_token', token);
		sessionStorage.setItem('auth_user', JSON.stringify(user));

		// Set user context in Sentry
		setUser({
			id: user.id,
			email: user.email,
			name: user.name,
			roles: user.roles,
		});

		setAuthState({
			isAuthenticated: true,
			user,
			token,
			isLoading: false,
		});
	};

	/**
	 * Clear authentication state
	 */
	const clearAuth = (): void => {
		// Clear stored data
		sessionStorage.removeItem('auth_token');
		sessionStorage.removeItem('auth_user');

		// Clear user context in Sentry
		clearUser();

		setAuthState({
			isAuthenticated: false,
			user: null,
			token: null,
			isLoading: false,
		});
	};

	// Role checking functions
	const hasRole = (role: string): boolean => {
		return authState.user?.roles?.includes(role) || false;
	};

	const hasAnyRole = (roles: string[]): boolean => {
		return roles.some(role => hasRole(role));
	};

	const hasPermission = (permission: string): boolean => {
		if (!authState.user?.roles) return false;

		// Admin has all permissions
		if (hasRole('ADMIN')) return true;

		// Map roles to permissions
		const rolePermissions: Record<string, string[]> = {
			READ: ['workflows.view', 'executions.view', 'folders.view'],
			EXECUTE: ['workflows.view', 'workflows.execute', 'executions.view', 'executions.retry'],
			WRITE: [
				'workflows.view',
				'workflows.create',
				'workflows.edit',
				'workflows.execute',
				'folders.create',
				'folders.edit',
			],
			SUPPORT: [
				'workflows.view',
				'executions.view',
				'executions.retry',
				'logs.view.detailed',
			],
			ADMIN: ['*'], // All permissions
		};

		return authState.user.roles.some(
			role =>
				rolePermissions[role]?.includes(permission) || rolePermissions[role]?.includes('*')
		);
	};

	const isAdmin = (): boolean => hasRole('ADMIN');
	const canWrite = (): boolean => hasAnyRole(['WRITE', 'ADMIN']);
	const canExecute = (): boolean => hasAnyRole(['EXECUTE', 'WRITE', 'SUPPORT', 'ADMIN']);
	const canDelete = (): boolean => hasRole('ADMIN'); // Only ADMIN users can delete workflows

	const contextValue: AuthContextType = {
		...authState,
		login,
		logout,
		setAuth,
		clearAuth,
		hasRole,
		hasAnyRole,
		hasPermission,
		isAdmin,
		canWrite,
		canExecute,
		canDelete,
	};

	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use authentication context
 *
 * @returns Authentication context
 * @throws Error if used outside AuthProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, login, logout } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <LoginButton onClick={() => login(googleIdToken)} />;
 *   }
 *
 *   return <div>Welcome, {user.name}!</div>;
 * }
 * ```
 */
export function useAuth(): AuthContextType {
	const context = useContext(AuthContext);

	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
}
